import React, { useContext, useEffect } from 'react';
import {
  Button, Image, Link, Text, Title1,
} from '@fluentui/react-components';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ReactTemplate } from '@bingads-webui-react/react-template';
import type {
  ActivationOptionsResponse,
  InvitationActivateResponse,
  InvitationValidationResponse,
} from '@bingads-webui-campaign-react/accept-invitation-api';
import { useAcceptInvitationStyles } from './styles';
import AllSetImage from '../../media/all-set-image.svg';

export type ActivationConfirmationProps = {
  activationResponse: InvitationActivateResponse;
  validation?: InvitationValidationResponse;
  activationOptions?: ActivationOptionsResponse;
  // Called (once, on mount) with a non-empty Validate `roleName` that getRoleDisplay doesn't map,
  // so the orchestrator can emit an instrumentation event flagging a new/renamed server role. The
  // page still renders the "Standard" fallback regardless.
  // eslint-disable-next-line no-unused-vars
  onUnmappedRole?: (roleName: string) => void;
};

// Number of access entries (customer or account) shown before collapsing the rest into a "+N" line.
const ENTRY_PREVIEW_LIMIT = 3;

// Accounts performance page path. The "View all account access" link reuses the post-activation
// redirect URL (the "Go to Microsoft Advertising" target) — keeping its origin and query params —
// but swaps the path to this accounts page.
// TODO [yaoyao]: Replace this hard-coded path logic with the site map API.
const ACCOUNTS_PERFORMANCE_PATH = '/campaign/vnext/accounts/performance';

const buildViewAllAccountsUrl = (redirectUrl: string): string => {
  try {
    const url = new URL(redirectUrl, window.location.origin);
    url.pathname = ACCOUNTS_PERFORMANCE_PATH;
    return url.toString();
  } catch {
    return ACCOUNTS_PERFORMANCE_PATH;
  }
};

// "Selected billing tasks" help article, shown inline in the Standard permission description. The
// locale segment is replaced at runtime with the current UI locale from server context.
const BILLING_TASKS_HELP_ARTICLE = '52037/0';

// Derives the help-center locale segment from the server-provided CurrentLocale (e.g. "en-US" ->
// "en"), defaulting to English. Help center deep-links are locale-scoped (…/apex/ads/{locale}/…).
const getHelpLocale = (): string => {
  const locale = (window as any)?.serverContext?.CurrentLocale;
  if (typeof locale === 'string' && locale) {
    return locale.toLowerCase().split('-')[0];
  }
  return 'en';
};

type RoleDisplay = {
  label: string;
  description: string;
  // Standard role shows a "some billing details" help link inline in its description.
  showBillingLink: boolean;
};

// The role display name the Validate API returns in `roleName` (verified against real responses).
// Used as both the display map keys and the "known" set, so the string literals live in one place.
const RoleName = {
  superAdmin: 'Super Admin',
  advertiserCampaignManager: 'Advertiser Campaign Manager',
  clientViewer: 'Client Viewer',
  standardUser: 'Standard User',
} as const;

// The Standard copy — also the fallback for an unknown / null roleName so the section always
// renders something meaningful.
const STANDARD_ROLE_DISPLAY: RoleDisplay = {
  label: _TL_('Standard'),
  description: _TL_('Manage campaigns and selected billing tasks for assigned accounts'),
  showBillingLink: true,
};

// Maps each known Validate `roleName` to a localized permission label + description.
const ROLE_DISPLAY_BY_NAME: Record<string, RoleDisplay> = {
  [RoleName.superAdmin]: {
    label: _TL_('Super'),
    description: _TL_('Manage the manager account, including all current and future accounts'),
    showBillingLink: false,
  },
  [RoleName.advertiserCampaignManager]: {
    label: _TL_('Campaign'),
    description: _TL_('Create and manage campaigns for assigned accounts'),
    showBillingLink: false,
  },
  [RoleName.clientViewer]: {
    label: _TL_('View only'),
    description: _TL_('View account details and campaign performance for assigned accounts'),
    showBillingLink: false,
  },
  [RoleName.standardUser]: STANDARD_ROLE_DISPLAY,
};

// The role names we map 1:1, derived from the display map so there's a single source of truth. A
// roleName outside this set still renders the "Standard" fallback, but is reported (see below) so a
// new/renamed server value is noticeable and the mapping can be updated.
const KNOWN_ROLE_NAMES = new Set<string>(Object.keys(ROLE_DISPLAY_BY_NAME));

// Looks up the localized display for a Validate `roleName`, falling back to the Standard copy for an
// unknown / null value.
const getRoleDisplay = (roleName?: string | null): RoleDisplay =>
  (roleName && ROLE_DISPLAY_BY_NAME[roleName]) || STANDARD_ROLE_DISPLAY;

// Maps the server-provided identity-provider code to a localized display label.
// MSA and AAD both surface as "Microsoft"; Google / Facebook keep their own names.
// Microsoft is only assumed when the value is missing; an unrecognized provider
// is shown as "Other" rather than being mislabeled as Microsoft.
const getIdentityTypeLabel = (i18n: any, identityType?: string): string => {
  if (!identityType) {
    return i18n.getString(_TL_('Microsoft'));
  }

  switch (identityType) {
    case 'Google':
      return i18n.getString(_TL_('Google'));
    case 'Facebook':
      return i18n.getString(_TL_('Facebook'));
    case 'MSA':
    case 'AAD':
    case 'Microsoft':
      return i18n.getString(_TL_('Microsoft'));
    default:
      return i18n.getString(_TL_('Other'));
  }
};

// Builds the confirmation intro sentence: a single entity (customer-level, or a lone account), or an
// entity followed by an "N other accounts" tail so the whole sentence stays one translatable unit.
const getAccessIntroText = (i18n: any, name: string, otherAccountsCount: number): string => {
  if (otherAccountsCount <= 0) {
    return i18n.getString(_TL_('You now have access to {{name}} on Microsoft Advertising.'), { name });
  }
  if (otherAccountsCount === 1) {
    return i18n.getString(_TL_('You now have access to {{name}} and 1 other account on Microsoft Advertising.'), { name });
  }
  return i18n.getString(_TL_('You now have access to {{name}} and {{count}} other accounts on Microsoft Advertising.'), { name, count: otherAccountsCount });
};

export const ActivationConfirmation: React.FC<ActivationConfirmationProps> = ({
  activationResponse,
  validation,
  activationOptions,
  onUnmappedRole,
}) => {
  const i18n = useContext(I18nContext) as any;
  const classes = useAcceptInvitationStyles();

  // Everything on this page is sourced from the Validate response (passed in, and persisted across a
  // browser refresh by the orchestrator) plus the Activate response — there is no AccessSummary fetch.
  const fallbackCustomerName = activationResponse.customerName || String(activationResponse.customerId || '');
  const customerName = validation?.customerName || fallbackCustomerName;
  // "Sign-in email" is the identity the user authenticated with — the Activate response's username
  // (server-set from the authenticated identity), not the invited email.
  const username = activationResponse.username
    || activationOptions?.username
    || validation?.email
    || '';
  // The "Sign-in type" is the provider the user actually signed in with, sourced from the
  // ActivationOptions API's identityType (Google / Facebook / MSA / AAD). Never derived from the URL.
  const identityType = getIdentityTypeLabel(i18n, activationOptions?.identityType);
  const redirectUrl = activationResponse.redirectUrl || '/login/live';
  // "View all account access" navigates to the accounts page, reusing the redirect URL's origin and
  // query params (see buildViewAllAccountsUrl). Only shown alongside the collapsed "+N" line.
  const viewAllAccountsUrl = buildViewAllAccountsUrl(redirectUrl);
  // Locale-scoped help deep-link for the Standard "selected billing tasks" inline link.
  const billingTasksHelpUrl = `https://help.ads.microsoft.com/#apex/ads/${getHelpLocale()}/${BILLING_TASKS_HELP_ARTICLE}`;

  // "You can access": account-level lists the Validate `accounts[].name (.number)` (first few, then a
  // "+N" collapse using the true total `accountCount`); customer-level shows the invited customer name
  // with its number — the Validate `customerNumber` (falling back to the Activate response's, which is
  // usually null).
  const isCustomerLevelAccess = validation?.isCustomerLevelAccess === true;
  const accounts = validation?.accounts || [];
  const customerNumber = validation?.customerNumber || activationResponse.customerNumber;
  const customerAccessEntry = customerNumber
    ? `${customerName} (${customerNumber})`
    : customerName;
  const shownAccessEntries = isCustomerLevelAccess
    ? [customerAccessEntry].filter(Boolean)
    : accounts.slice(0, ENTRY_PREVIEW_LIMIT).map(account => (account.number ? `${account.name} (${account.number})` : account.name));
  const remainingAccounts = isCustomerLevelAccess
    ? 0
    : Math.max(0, (validation?.accountCount ?? accounts.length) - shownAccessEntries.length);
  const hasAccessList = shownAccessEntries.length > 0;

  // Confirmation intro entity: customer-level shows the customer name; account-level shows the first
  // account name plus an "N other accounts" tail (name-only), mirroring the access-section overflow.
  const introEntityName = isCustomerLevelAccess ? customerName : (accounts[0]?.name || customerName);
  const otherAccountsCount = isCustomerLevelAccess
    ? 0
    : Math.max(0, (validation?.accountCount ?? accounts.length) - 1);
  const accessIntroText = getAccessIntroText(i18n, introEntityName, otherAccountsCount);

  const roleDisplay = getRoleDisplay(validation?.roleName);

  // If the server sends a roleName we don't map, we still show the "Standard" fallback — but report
  // it through our instrumentation (see onUnmappedRole / the orchestrator's telemetry) so a
  // new/renamed role value is noticed and the mapping can be updated. Only fires for a non-empty,
  // unrecognized value.
  useEffect(() => {
    const roleName = validation?.roleName;
    if (roleName && !KNOWN_ROLE_NAMES.has(roleName)) {
      onUnmappedRole?.(roleName);
    }
  }, [validation?.roleName, onUnmappedRole]);

  return (
    <div className={classes.centeredPage}>
      <div id="activation-confirmation" className={classes.confirmation}>
        <Image className={classes.celebration} src={AllSetImage} alt="" aria-hidden="true" />
        <Title1 as="h1">{i18n.getString(_TL_('You\'re all set'))}</Title1>
        <Text block className={classes.confirmationText}>{accessIntroText}</Text>
        <Text block className={classes.confirmationText}>{i18n.getString(_TL_('Here are your account details.'))}</Text>

        <div className={classes.summaryCard}>
          <div className={classes.summaryRow}>
            <Text>{i18n.getString(_TL_('Sign-in email'))}</Text>
            <Text className={classes.summaryValue}>{username}</Text>
          </div>
          <div className={classes.summaryRow}>
            <Text>{i18n.getString(_TL_('Sign-in type'))}</Text>
            <Text className={classes.summaryValue}>{identityType}</Text>
          </div>
          <div className={classes.summaryRow}>
            <Text>{i18n.getString(_TL_('Account access'))}</Text>
            {hasAccessList ? (
              <div className={classes.accessList}>
                {shownAccessEntries.map((entry, index) => (
                  // Entries are generally unique; fall back to the index for the rare dup.
                  // eslint-disable-next-line react/no-array-index-key
                  <Text key={`${entry}-${index}`} className={classes.accessEntry} weight="semibold">
                    {entry}
                  </Text>
                ))}
                {remainingAccounts > 0 && (
                  <>
                    <Text className={classes.accessMore} weight="semibold">
                      {i18n.getString(_TL_('+{{count}}'), { count: remainingAccounts })}
                    </Text>
                    <Link className="view-all-account-access" href={viewAllAccountsUrl}>
                      {i18n.getString(_TL_('View all account access'))}
                    </Link>
                  </>
                )}
              </div>
            ) : (
              <Text className={classes.summaryValue}>{customerAccessEntry}</Text>
            )}
          </div>
          <div className={classes.summaryRow}>
            <Text>{i18n.getString(_TL_('Permission'))}</Text>
            <div className={classes.permissionValue}>
              <Text block weight="semibold">{i18n.getString(roleDisplay.label)}</Text>
              {roleDisplay.showBillingLink ? (
                <Text block size={200}>
                  <ReactTemplate
                    template={i18n.getString(_TL_('Manage campaigns and {{a}}selected billing tasks{{/a}} for assigned accounts'))}
                    model={{
                      a: (text: string) => (
                        <Link href={billingTasksHelpUrl} target="_blank" rel="noopener noreferrer" inline>
                          {text}
                        </Link>
                      ),
                    }}
                  />
                </Text>
              ) : (
                <Text block size={200}>{i18n.getString(roleDisplay.description)}</Text>
              )}
            </div>
          </div>
        </div>

        <Button className="activation-go-to-ads" appearance="primary" onClick={() => window.location.assign(redirectUrl)}>
          {i18n.getString(_TL_('Go to Microsoft Advertising'))}
        </Button>
      </div>
    </div>
  );
};
